import { MIPManageInvestorsV2Context } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/MIPManageInvestorsV2Context';
import { SHARES_MODAL_MODE } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/constants';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { NewJoinersAndAllocationsModalContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsModal';
import { NewJoinersAndAllocationsInvestorLocal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/useNewJoinersAndAllocationsModalController';
import {
	downloadUpsertSharesInvestorsExcelTemplate,
	uploadJoinerInvestorsExcel,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	DataJoinerInvestorGetTemplateDto,
	PostInvestorJoinerStatistics,
} from '@dealstack/shared/lib/generated';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import { useContext, useEffect, useState } from 'react';

export const useNewJoinersAndAllocationsInvestorsController = () => {
	const tDefault = useTranslationDefault();

	const { mipData, deal } = useMIPManageContext();

	const { investorsForm, allSecurities, allLoansDefinitions } = useContext(
		NewJoinersAndAllocationsModalContext
	);
	const manageInvestorsControllerV2 = useContext(MIPManageInvestorsV2Context);
	const uploadInvestorsTemplate = useWrappedRequest(uploadJoinerInvestorsExcel);
	const downloadInvestorsExcelTemplateRequest = useWrappedRequest(
		downloadUpsertSharesInvestorsExcelTemplate
	);

	const [investorsList, setInvestorsList] = useState<NewJoinersAndAllocationsInvestorLocal[]>([]);
	const [investorsSummary, setInvestorsSummary] = useState<PostInvestorJoinerStatistics>();

	useEffect(() => {
		const oldData = investorsForm.getValues();
		if (oldData?.investorsListData?.length) {
			setInvestorsList(oldData.investorsListData);
		}
		if (oldData?.summary) {
			setInvestorsSummary(oldData.summary);
		}
	}, []);

	useEffect(() => {
		const payload = uploadInvestorsTemplate?.response?.payload;
		if (!payload) {
			return;
		}

		const investorsListData = payload.data;
		const investorsSummaryData = payload.statistics;
		const convertedInvestors: NewJoinersAndAllocationsInvestorLocal[] =
			investorsListData?.map((investor) => {
				return {
					...investor,
					id: generateUUID(),
					contracts: [],
				};
			}) ?? [];

		if (convertedInvestors?.length) {
			setInvestorsList(convertedInvestors);
		}
		if (investorsSummaryData) {
			setInvestorsSummary(investorsSummaryData);
		}

		if (!payload.isValid) {
			showToastV2({
				title: tDefault('warning'),
				message: tDefault('invalid_template'),
				state: 'warning',
			});
		}
	}, [uploadInvestorsTemplate?.response?.payload]);

	useEffect(() => {
		if (!investorsSummary) {
			return;
		}
		investorsForm.setValue('summary', investorsSummary);
	}, [investorsSummary]);

	useEffect(() => {
		if (!investorsList) {
			return;
		}
		investorsForm.setValue('investorsListData', investorsList);
	}, [investorsList]);

	const onTemplateDownload = () => {
		if (!mipData) {
			return;
		}

		const bulkActionPayload = manageInvestorsControllerV2.bulkActionPayload;
		const useBulkAction =
			!!bulkActionPayload &&
			manageInvestorsControllerV2.investorSharesModalMode ===
				SHARES_MODAL_MODE.ADD_ALLOCATION;
		const investorsIds =
			manageInvestorsControllerV2.investorSharesModalMode === SHARES_MODAL_MODE.ADD_ALLOCATION
				? manageInvestorsControllerV2.selectedInvestors
						?.filter((item) => item.isEligibleForAllocations)
						.map((item) => item.id) ?? []
				: [];
		const localSecurities = allSecurities
			?.filter(({ locked }) => !locked)
			.map((item) => ({
				...item,
				issuerId: mipData.entityId,
				currency: mipData.currency,
			}));

		const localLoansDefinitions = allLoansDefinitions.filter(({ locked }) => !locked);

		const templateData: DataJoinerInvestorGetTemplateDto = {
			investorId: useBulkAction ? [] : investorsIds,
			bulkAction: useBulkAction ? bulkActionPayload : undefined,
			pendingSecurities: localSecurities,
			loansDefinitions: localLoansDefinitions,
		};

		downloadInvestorsExcelTemplateRequest.fetch(templateData).onSuccess((payload) => {
			if (!payload) {
				return;
			}
			const templateName = `${deal?.name}-${mipData?.entityName ?? ''}-investors-template.xlsx`;

			downloadFileFromBlob(payload, templateName);
		});
	};

	return {
		uploadInvestorsTemplate,
		investorsList,
		setInvestorsList,
		investorsSummary,
		onTemplateDownload,
	};
};
